/*
 * Providers Panel — ASTAT admin
 * - Create / edit TPs (contract rules incl. dispatch_lead_time, auto-assign)
 * - View roster per TP
 * - Review + approve/reject TP applications
 * - Create TP dispatcher user accounts (for /tp login)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import PhoneInput, { isValidE164 } from "../../components/PhoneInput";
import SMSComplianceNotice from "../../components/SMSComplianceNotice";
import { confirm } from "../../components/ConfirmModal";
import { Copy, CheckCircle2, ShieldCheck, ShieldAlert, ChevronRight, QrCode, X, Clock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { ZoneOverrideSelect } from "../../components/ZoneBadge";

const TP = "/tpt";

const num = (v, d = 0) => (v === "" || v === null || v === undefined ? d : Number(v));

function JumpInButton({ tp }) {
  const [busy, setBusy] = useState(false);
  const jumpIn = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`${TP}/providers/${tp.tp_id}/impersonate`);
      window.open(data.portal_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not jump in");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={jumpIn} disabled={busy} className="border border-brandblue/40 text-brandblue px-3 py-1 rounded-md" data-testid={`jump-in-tp-${tp.tp_id}`} title="Open this TP's portal as them, in a new tab">
      {busy ? "Opening…" : "Jump in →"}
    </button>
  );
}


function PortalTierDialog({ tp, onClose }) {
  const [data, setData] = useState(null);
  const [tier, setTier] = useState("free");
  const [enabled, setEnabled] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!tp) return;
    try {
      const { data } = await apiClient.get(`${TP}/providers/${tp.tp_id}/portal-tier`);
      setData(data);
      setTier(data.tier);
      setEnabled(data.enabled_features || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Load failed"); }
  }, [tp]);
  useEffect(() => { load(); }, [load]);
  if (!tp) return null;

  const toggleFeature = (key) => {
    setEnabled((cur) => cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);
  };
  const totalCents = (data?.features || []).filter((f) => enabled.includes(f.key)).reduce((s, f) => s + f.price_cents, 0);
  const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  const save = async () => {
    setBusy(true);
    try {
      const { data: res } = await apiClient.put(`${TP}/providers/${tp.tp_id}/portal-tier`, { tier, enabled_features: tier === "paid" ? enabled : [] });
      toast.success(`${tp.name} → ${tier === "paid" ? `PAID (${usd(res.total_cents)}/mo)` : "FREE"}`);
      if (res.hosted_invoice_url) {
        toast.info("Payment link generated — send it to the TP to activate billing", { duration: 8000 });
        window.open(res.hosted_invoice_url, "_blank", "noopener,noreferrer");
      }
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="portal-tier-dialog">
        <div className="text-lg font-bold mb-1">Portal tier · {tp.name}</div>
        <div className="text-xs text-slate-500 mb-4">FREE always includes Offers, Live Trips, History, Drivers, Documents. Toggle PAID add-ons à la carte — prices are set in the catalog below and sum to this TP's custom monthly package.</div>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setTier("free")} data-testid="portal-tier-set-free"
            className={`flex-1 py-2 rounded-md text-sm font-semibold border ${tier === "free" ? "bg-forest text-white border-forest" : "border-[#E2E0D8]"}`}>FREE</button>
          <button onClick={() => setTier("paid")} data-testid="portal-tier-set-paid"
            className={`flex-1 py-2 rounded-md text-sm font-semibold border ${tier === "paid" ? "bg-amber-600 text-white border-amber-600" : "border-[#E2E0D8]"}`}>PAID</button>
        </div>

        {tier === "paid" && (
          <div className="space-y-2 mb-4">
            {(data?.features || []).map((f) => (
              <label key={f.key} className="flex items-center justify-between gap-2 border border-[#E2E0D8] rounded-md px-3 py-2 cursor-pointer" data-testid={`portal-feature-row-${f.key}`}>
                <span className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={enabled.includes(f.key)} onChange={() => toggleFeature(f.key)} data-testid={`portal-feature-toggle-${f.key}`} />
                  {f.label}
                </span>
                <span className="text-xs font-semibold text-slate-500">{usd(f.price_cents)}/mo</span>
              </label>
            ))}
          </div>
        )}

        {tier === "paid" && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm font-semibold text-amber-800 mb-4" data-testid="portal-tier-total">
            Custom package total: {usd(totalCents)}/mo
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold" data-testid="portal-tier-save">
            {busy ? "Saving…" : "Save tier"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SubmitTPButton({ tp }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/medexova/tps/${tp.tp_id}/submit`);
      if (data.ok) toast.success(`Submitted to MedexOva · status: ${data.status}`);
      else toast.error(data.error?.detail || "MedexOva submit failed");
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Submit failed";
      toast.error(msg.length > 140 ? msg.slice(0, 140) + "…" : msg);
    } finally { setBusy(false); }
  };
  const status = tp.medexova_tp_status || "not_submitted";
  const tone = status === "cleared" ? "bg-green-50 text-green-800 border-green-200"
    : status === "rejected" || status === "lapsed" ? "bg-red-50 text-red-800 border-red-200"
    : status === "in_review" || status === "pending" ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <>
      <span className={`text-[10px] font-semibold px-2 py-1 rounded-md border ${tone}`} title="MedexOva TP credentialing status" data-testid={`tp-medexova-status-${tp.tp_id}`}>
        MedexOva: {status}
      </span>
      <button onClick={submit} disabled={busy} className="border border-[#E2E0D8] px-3 py-1 rounded-md hover:bg-slate-50" data-testid={`submit-tp-medexova-${tp.tp_id}`} title="Push this TP's credentialing dossier to MedexOva">
        {busy ? "Submitting…" : "Submit to MedexOva"}
      </button>
    </>
  );
}


function TempApproveButton({ tp, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const now = new Date();
  const until = tp.temp_cleared_until ? new Date(tp.temp_cleared_until) : null;
  const isActive = until && until > now;
  const daysLeft = isActive ? Math.ceil((until - now) / 86400000) : 0;

  const grant = async (days) => {
    setBusy(true);
    setShowPicker(false);
    try {
      const { data } = await apiClient.post(`${TP}/providers/${tp.tp_id}/temp-approve`, { days });
      toast.success(`Temp approval granted — ${data.days} days, expires ${new Date(data.temp_cleared_until).toLocaleDateString()}`);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Temp approve failed");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await apiClient.delete(`${TP}/providers/${tp.tp_id}/temp-approve`);
      toast.success("Temp approval revoked");
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Revoke failed");
    } finally { setBusy(false); }
  };

  if (isActive) {
    return (
      <div className="inline-flex items-center gap-1">
        <span
          className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border bg-amber-50 text-amber-800 border-amber-300"
          title={`Temp approved until ${until.toLocaleString()} — bypasses MedexOva credential gate`}
          data-testid={`tp-temp-approved-${tp.tp_id}`}
        >
          <Clock size={9}/> Temp approved · {daysLeft}d left
        </span>
        <button
          onClick={revoke} disabled={busy}
          className="border border-red-200 text-red-600 px-2 py-1 rounded-md hover:bg-red-50 text-[10px]"
          data-testid={`revoke-temp-approve-${tp.tp_id}`}
          title="Revoke temporary approval"
        >
          {busy ? "…" : "Revoke"}
        </button>
      </div>
    );
  }

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setShowPicker(v => !v)} disabled={busy}
        className="border border-amber-300 text-amber-700 px-3 py-1 rounded-md hover:bg-amber-50"
        data-testid={`temp-approve-tp-${tp.tp_id}`}
        title="Grant a temporary dispatch bypass while MedexOva credentialing is pending/rejected"
      >
        {busy ? "Approving…" : "Temp Approve"}
      </button>
      {showPicker && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-[#E2E0D8] rounded-md shadow-lg z-50 py-1 min-w-[140px]">
          <div className="px-3 py-1 text-[10px] text-slate-500 font-semibold uppercase tracking-wide border-b border-[#E2E0D8] mb-1">
            Grant bypass for
          </div>
          {[7, 14, 30, 60].map(d => (
            <button
              key={d} onClick={() => grant(d)}
              className="block w-full text-left px-3 py-1.5 text-xs hover:bg-amber-50 text-amber-800"
              data-testid={`temp-approve-days-${d}-${tp.tp_id}`}
            >
              {d} days
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function SendContractButton({ tp }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    apiClient.get(`${TP}/providers/${tp.tp_id}/contract`)
      .then(({ data }) => setStatus(data))
      .catch(() => {});
  }, [tp.tp_id]);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`${TP}/providers/${tp.tp_id}/contract/send-link`);
      toast.success(`Contract link emailed to ${data.recipient}`);
    } catch (e) {
      // Fallback: copy link even when email fails
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Send failed — no email on file?");
    } finally { setBusy(false); }
  };
  const copyLink = async () => {
    try {
      const { data } = await apiClient.post(`${TP}/providers/${tp.tp_id}/contract/send-link`);
      await navigator.clipboard.writeText(data.link);
      toast.success("Link copied");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  if (status?.signed) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-green-50 text-green-800 border border-green-200" data-testid={`contract-signed-${tp.tp_id}`}>
        <CheckCircle2 size={11}/> Contract signed
        {status.signer_name && <span className="text-green-700 font-normal">· {status.signer_name}</span>}
      </span>
    );
  }
  return (
    <div className="inline-flex gap-1">
      <button onClick={send} disabled={busy} className="border border-[#E2E0D8] px-3 py-1 rounded-md hover:bg-slate-50" data-testid={`send-contract-${tp.tp_id}`}>
        {busy ? "Sending…" : "Send contract"}
      </button>
      <button onClick={copyLink} className="border border-[#E2E0D8] px-2 py-1 rounded-md hover:bg-slate-50" title="Copy signing link" data-testid={`copy-contract-link-${tp.tp_id}`}>
        <Copy size={12}/>
      </button>
    </div>
  );
}


function OnboardingChip({ tpId, onOpen }) {  const [state, setState] = useState(null);
  useEffect(() => {
    let cancel = false;
    apiClient.get(`${TP}/providers/${tpId}/onboarding`)
      .then(({ data }) => { if (!cancel) setState(data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [tpId]);
  if (!state) return null;
  const { completed, total, ready, driver_counts } = state;
  const cls = ready
    ? "bg-green-50 text-green-800 border-green-200"
    : (driver_counts?.pending > 0 || completed < total)
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : "bg-slate-50 text-slate-700 border-slate-200";
  const Icon = ready ? ShieldCheck : ShieldAlert;
  return (
    <button
      onClick={() => onOpen(state)}
      className={`w-full mt-3 inline-flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-xs font-medium ${cls}`}
      data-testid={`tp-onboarding-chip-${tpId}`}
    >
      <span className="inline-flex items-center gap-1.5">
        <Icon size={14}/>
        Onboarding {completed}/{total}
        {driver_counts?.pending > 0 && (
          <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/70">
            {driver_counts.pending} driver{driver_counts.pending === 1 ? "" : "s"} pending
          </span>
        )}
      </span>
      <ChevronRight size={14}/>
    </button>
  );
}

function OnboardingDialog({ state, onClose }) {
  if (!state) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="tp-onboarding-dialog">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-forest"/>
          <div className="text-lg font-bold text-slate_navy">Onboarding · {state.tp_name}</div>
        </div>
        <div className="text-xs text-slate-500 mb-4">
          {state.ready
            ? "This TP is fully onboarded and broker-compliant."
            : `${state.completed} of ${state.total} steps complete. Items below block full compliance.`}
        </div>
        <ul className="space-y-2 mb-4">
          {state.checklist.map((c) => (
            <li key={c.key} className={`flex items-start gap-2 p-2 rounded-md border ${c.ok ? "bg-green-50 border-green-100" : "bg-amber-50 border-amber-100"}`} data-testid={`onboarding-item-${c.key}`}>
              {c.ok ? <CheckCircle2 size={16} className="text-green-600 mt-0.5 shrink-0"/> : <ShieldAlert size={16} className="text-amber-600 mt-0.5 shrink-0"/>}
              <div className="text-xs">
                <div className="font-semibold text-slate-900">{c.label}</div>
                <div className="text-slate-600">{c.detail}</div>
              </div>
            </li>
          ))}
        </ul>
        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600 mb-3">
          <b>Driver credentialing summary:</b>{" "}
          {state.driver_counts.cleared} cleared · {state.driver_counts.pending} pending · {state.driver_counts.rejected} rejected · {state.driver_counts.total} total
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold">Close</button>
        </div>
      </div>
    </div>
  );
}

function TPEditor({ tp, onClose, onSaved }) {
  const initial = tp || {
    name: "", contact_name: "", contact_phone: "", contact_email: "", ein: "",
    wait_time_minutes: 5, no_show_wait_minutes: 5, offer_timeout_seconds: 300,
    pays_dead_miles: false, dead_mile_rate: 0,
    dispatch_lead_time_minutes: 60, auto_assign_enabled: false, auto_assign_strategy: "least_recent",
    status: "active",
    service_area_type: "radius", service_zip_codes: [], service_radius_miles: 10,
    service_home_address: "",
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        wait_time_minutes: num(form.wait_time_minutes, 5),
        no_show_wait_minutes: num(form.no_show_wait_minutes, 5),
        offer_timeout_seconds: num(form.offer_timeout_seconds, 300),
        dead_mile_rate: num(form.dead_mile_rate, 0),
        dispatch_lead_time_minutes: num(form.dispatch_lead_time_minutes, 60),
        service_radius_miles: form.service_radius_miles === "" || form.service_radius_miles == null
          ? null : Number(form.service_radius_miles),
        service_zip_codes: (Array.isArray(form.service_zip_codes)
          ? form.service_zip_codes
          : String(form.service_zip_codes || "").split(/[,\s]+/)
        ).map((z) => String(z).trim()).filter((z) => /^\d{5}$/.test(z)),
      };
      if (tp?.tp_id) {
        await apiClient.patch(`${TP}/providers/${tp.tp_id}`, payload);
      } else {
        await apiClient.post(`${TP}/providers`, payload);
      }
      toast.success(tp?.tp_id ? "TP updated" : "TP created");
      onSaved(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };
  const renderF = (label, k, type = "text") => (
    <label className="block" key={k}>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <input type={type} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`tp-${k}`}
        className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
    </label>
  );
  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tp-editor">
        <div className="text-lg font-bold mb-1">{tp?.tp_id ? "Edit provider" : "New provider"}</div>
        <div className="text-xs text-slate-500 mb-4">Contract rules govern wait time, no-show, dispatch lead time, and auto-assignment.</div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Identity</div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {renderF("Company name", "name")}
          {renderF("EIN", "ein")}
          {renderF("Contact name", "contact_name")}
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Contact phone</div>
            <PhoneInput value={form.contact_phone} onChange={(v) => set("contact_phone", v)} testid="tp-contact_phone" />
          </label>
          {renderF("Contact email", "contact_email", "email")}
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Status</div>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm">
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </label>
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Contract rules</div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {renderF("Free wait time (min)", "wait_time_minutes", "number")}
          {renderF("No-show wait (min)", "no_show_wait_minutes", "number")}
          {renderF("Offer TTL (seconds)", "offer_timeout_seconds", "number")}
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Pays dead miles</div>
            <select value={form.pays_dead_miles ? "yes" : "no"} onChange={(e) => set("pays_dead_miles", e.target.value === "yes")} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm">
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </label>
          {renderF("Dead mile rate ($/mi)", "dead_mile_rate", "number")}
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Dispatch behavior</div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {renderF("Dispatch lead time (min)", "dispatch_lead_time_minutes", "number")}
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Auto-assign</div>
            <select value={form.auto_assign_enabled ? "on" : "off"} onChange={(e) => set("auto_assign_enabled", e.target.value === "on")} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm">
              <option value="off">Off — dispatcher picks manually</option>
              <option value="on">On — pick a driver at dispatch time</option>
            </select>
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Auto-assign strategy</div>
            <select value={form.auto_assign_strategy} onChange={(e) => set("auto_assign_strategy", e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm">
              <option value="least_recent">Least-recent driver (fairness)</option>
              <option value="round_robin">Round-robin</option>
            </select>
          </label>
        </div>
        <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600 mb-4">
          <b>Formula:</b> Dispatch SMS fires at <code>pickup − lead − est_duration</code>. Default {form.dispatch_lead_time_minutes || 60}min + trip duration before pickup.
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Service area (VA suggested-providers ranker)</div>
        <div className="grid grid-cols-2 gap-3 mb-2">
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Area type</div>
            <select
              value={form.service_area_type || "radius"}
              onChange={(e) => set("service_area_type", e.target.value)}
              data-testid="tp-service_area_type"
              className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"
            >
              <option value="radius">Radius from business address</option>
              <option value="zips">Specific ZIP codes only</option>
              <option value="both">Both</option>
            </select>
          </label>
          {(form.service_area_type !== "zips") && (
            renderF("Radius (miles)", "service_radius_miles", "number")
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 mb-4">
          {(form.service_area_type !== "zips") && (
            <label className="block">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Radius center address (blank = business/physical address)</div>
              <input
                type="text"
                value={form.service_home_address ?? ""}
                onChange={(e) => set("service_home_address", e.target.value)}
                data-testid="tp-service_home_address"
                placeholder="e.g. 8100 W 95th St, Chicago IL 60453"
                className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"
              />
            </label>
          )}
          {(form.service_area_type !== "radius") && (
            <label className="block">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Service ZIP codes (comma-separated 5-digit)</div>
              <input
                type="text"
                value={Array.isArray(form.service_zip_codes) ? form.service_zip_codes.join(", ") : (form.service_zip_codes || "")}
                onChange={(e) => set("service_zip_codes", e.target.value)}
                data-testid="tp-service_zip_codes"
                placeholder="60620, 60453, 60466"
                className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"
              />
            </label>
          )}
          {tp && (
            <ZoneOverrideSelect
              entityType="tp"
              entityId={tp.tp_id}
              zoneId={form.zone_id}
              zoneName={form.zone_name}
              zoneSource={form.zone_source}
              onSaved={() => onSaved?.()}
              testId="tp-zone-override"
            />
          )}
          {form.service_home_lat != null && form.service_home_lng != null && (
            <div className="text-[11px] text-slate-500">
              Geocoded: {Number(form.service_home_lat).toFixed(4)}, {Number(form.service_home_lng).toFixed(4)}
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Cancel</button>
          <button onClick={save} disabled={busy || !form.name} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold" data-testid="tp-save">
            {busy ? "Saving…" : "Save provider"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TPUserDialog({ tp, onClose }) {
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "dispatcher" });
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!tp) return;
    const r = await apiClient.get(`${TP}/providers/${tp.tp_id}/users`);
    setUsers(r.data || []);
  }, [tp]);
  useEffect(() => { load(); }, [load]);
  if (!tp) return null;
  const add = async () => {
    if (!form.email || !form.password) { toast.error("Email + password required"); return; }
    setBusy(true);
    try {
      await apiClient.post(`${TP}/providers/${tp.tp_id}/users`, form);
      toast.success("Dispatcher account created");
      setForm({ email: "", password: "", name: "", role: "dispatcher" });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="text-lg font-bold mb-1">Dispatchers · {tp.name}</div>
        <div className="text-xs text-slate-500 mb-4">These accounts log in at <code>/tp/login</code> and see only their TP&apos;s trips.</div>
        <div className="border border-[#E9EAE4] rounded-lg mb-4">
          {users.length === 0 && <div className="p-3 text-sm text-slate-500">No dispatchers yet.</div>}
          {users.map(u => (
            <div key={u.id} className="p-3 border-b border-[#E9EAE4] last:border-0 flex justify-between items-center text-sm">
              <div><b>{u.email}</b><div className="text-xs text-slate-500">{u.name || u.role} · {u.active ? "active" : "inactive"}</div></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email:e.target.value})} data-testid="tpuser-email" className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
          <input placeholder="Password" value={form.password} onChange={(e) => setForm({...form, password:e.target.value})} data-testid="tpuser-password" className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
          <input placeholder="Name (optional)" value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
          <select value={form.role} onChange={(e) => setForm({...form, role:e.target.value})} className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm">
            <option value="dispatcher">Dispatcher</option>
            <option value="manager">Manager</option>
          </select>
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Close</button>
          <button onClick={add} disabled={busy} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold" data-testid="tpuser-add">Add dispatcher</button>
        </div>
      </div>
    </div>
  );
}

function ApplicationsPanel({ applications, onChange }) {
  const [creds, setCreds] = useState(null); // shown once after approval
  const approve = async (a) => {
    const ok = await confirm({
      title: `Approve ${a.company_name}?`,
      body: "This creates a live Transportation Provider record and auto-generates a dispatcher login. You'll get the credentials to send to the applicant.",
      confirmLabel: "Approve TP",
      tone: "primary",
    });
    if (!ok) return;
    try {
      const r = await apiClient.post(`${TP}/applications/${a.application_id}/approve`);
      if (r.data?.temp_password) {
        setCreds({
          tp_name: r.data.tp_name,
          email: r.data.dispatcher_email,
          password: r.data.temp_password,
          portal_url: r.data.portal_url,
        });
      } else {
        toast.success("Approved — existing dispatcher account reused");
      }
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Approve failed"); }
  };
  const reject = async (a) => {
    const reason = window.prompt("Reason for rejection?");
    if (!reason) return;
    await apiClient.post(`${TP}/applications/${a.application_id}/reject`, { reason });
    toast.info("Rejected");
    onChange();
  };
  const copyAll = async () => {
    if (!creds) return;
    const text = `Welcome to MedexOVA Transportation!\n\nYour Transportation Provider account is live.\n\nPortal: ${creds.portal_url}\nEmail: ${creds.email}\nTemporary password: ${creds.password}\n\nYou will be prompted to change your password on first login.`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Credentials copied to clipboard");
    } catch (_) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Credentials copied to clipboard");
      } catch { toast.error("Copy failed — please copy manually"); }
    }
  };
  if (!applications || applications.length === 0) return creds ? <CredentialsModal creds={creds} onClose={() => setCreds(null)} onCopy={copyAll}/> : null;
  return (
    <div className="mb-8">
      <CredentialsModal creds={creds} onClose={() => setCreds(null)} onCopy={copyAll}/>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Pending applications</div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
        {applications.filter(a => a.status === "pending").map(a => (
          <div key={a.application_id} className="p-4 border-b border-amber-200 last:border-0 flex justify-between items-start" data-testid={`application-${a.application_id}`}>
            <div>
              <div className="font-semibold text-slate-900">{a.company_name}</div>
              <div className="text-sm text-slate-600">{a.contact_name} · {a.contact_phone} · {a.contact_email}</div>
              <div className="text-xs text-slate-500 mt-1">Fleet: {a.fleet_size || "?"} · Vehicles: {(a.vehicle_types || []).join(", ") || "?"} · Area: {a.service_area || "?"}</div>
              {a.notes && <div className="text-xs text-slate-500 italic mt-1">{a.notes}</div>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => approve(a)} className="text-xs bg-forest text-white px-3 py-1.5 rounded-md" data-testid={`approve-${a.application_id}`}>Approve</button>
              <button onClick={() => reject(a)} className="text-xs border border-[#E2E0D8] px-3 py-1.5 rounded-md">Reject</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MissingVehicleInfoPanel({ drivers, onChanged }) {
  const [edits, setEdits] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [open, setOpen] = useState(true);
  const incomplete = useMemo(
    () => (drivers || []).filter(d => d.status !== "deactivated" && (!d.vehicle_make || !d.vehicle_model || !d.vehicle_vin || !d.assigned_vehicle_plate)),
    [drivers]
  );
  const field = (d, k) => edits[d.id]?.[k] ?? d[k] ?? "";
  const setField = (d, k, v) => setEdits(cur => ({ ...cur, [d.id]: { ...cur[d.id], [k]: v } }));

  const save = async (d) => {
    const patch = edits[d.id];
    if (!patch) return;
    setBusyId(d.id);
    try {
      await apiClient.patch(`/drivers/${d.id}`, {
        vehicle_make: patch.vehicle_make ?? d.vehicle_make,
        vehicle_model: patch.vehicle_model ?? d.vehicle_model,
        vehicle_year: patch.vehicle_year ? Number(patch.vehicle_year) : (d.vehicle_year || undefined),
        vehicle_vin: (patch.vehicle_vin ?? d.vehicle_vin)?.toUpperCase(),
        assigned_vehicle_plate: (patch.assigned_vehicle_plate ?? d.assigned_vehicle_plate)?.toUpperCase(),
      });
      toast.success(`${d.name}: vehicle info saved`);
      try {
        await apiClient.post(`/medexova/drivers/${d.id}/submit`);
        toast.success(`${d.name}: submitted to MedexOva`);
      } catch (e) {
        // Expected if required docs are still missing — vehicle data is saved regardless.
        toast.info(formatApiErrorDetail(e?.response?.data?.detail) || `${d.name}: vehicle saved, not auto-submitted yet`);
      }
      setEdits(cur => { const n = { ...cur }; delete n[d.id]; return n; });
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setBusyId(null); }
  };

  const uploadPhoto = async (d, file) => {
    if (!file) return;
    setUploadingId(d.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post(`/tpt/drivers/${d.id}/vehicle-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ocr = data?.ocr || {};
      if (ocr.vin && !field(d, "vehicle_vin")) setField(d, "vehicle_vin", ocr.vin);
      if (ocr.plate && !field(d, "assigned_vehicle_plate")) setField(d, "assigned_vehicle_plate", ocr.plate);
      if (ocr.vin || ocr.plate) {
        toast.success(`${d.name}: photo attached — read ${[ocr.vin && "VIN", ocr.plate && "plate"].filter(Boolean).join(" + ")} off the photo, double-check before saving`);
      } else {
        toast.success(`${d.name}: photo attached — couldn't read VIN/plate clearly, fill in manually`);
      }
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Photo upload failed");
    } finally { setUploadingId(null); }
  };

  const viewPhoto = async (d) => {
    try {
      const res = await apiClient.get(`/tpt/drivers/${d.id}/vehicle-photo`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error("Could not load photo");
    }
  };

  if (incomplete.length === 0) return null;
  return (
    <div className="mb-8" data-testid="missing-vehicle-info-panel">
      <button className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1" onClick={() => setOpen(o => !o)} data-testid="missing-vehicle-info-toggle">
        Missing vehicle info ({incomplete.length}) <ChevronRight className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl overflow-x-auto">
          <table className="w-full text-xs">
            <thead>

              <tr className="text-left text-orange-800 border-b border-orange-200">
                <th className="p-2">Driver</th><th className="p-2">Make</th><th className="p-2">Model</th>
                <th className="p-2">Year</th><th className="p-2">VIN</th><th className="p-2">Plate</th><th className="p-2">Photo</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {incomplete.map(d => (
                <tr key={d.id} className="border-b border-orange-100 last:border-0" data-testid={`missing-vehicle-row-${d.id}`}>
                  <td className="p-2 font-semibold text-slate-800 whitespace-nowrap">{d.name}</td>
                  <td className="p-1"><input value={field(d, "vehicle_make")} onChange={e => setField(d, "vehicle_make", e.target.value)} placeholder="Make" className="w-24 border border-orange-200 rounded px-1.5 py-1" data-testid={`vehicle-make-input-${d.id}`}/></td>
                  <td className="p-1"><input value={field(d, "vehicle_model")} onChange={e => setField(d, "vehicle_model", e.target.value)} placeholder="Model" className="w-24 border border-orange-200 rounded px-1.5 py-1" data-testid={`vehicle-model-input-${d.id}`}/></td>
                  <td className="p-1"><input value={field(d, "vehicle_year")} onChange={e => setField(d, "vehicle_year", e.target.value)} placeholder="Year" type="number" className="w-16 border border-orange-200 rounded px-1.5 py-1" data-testid={`vehicle-year-input-${d.id}`}/></td>
                  <td className="p-1"><input value={field(d, "vehicle_vin")} onChange={e => setField(d, "vehicle_vin", e.target.value.toUpperCase())} placeholder="VIN" className="w-32 border border-orange-200 rounded px-1.5 py-1" data-testid={`vehicle-vin-input-${d.id}`}/></td>
                  <td className="p-1"><input value={field(d, "assigned_vehicle_plate")} onChange={e => setField(d, "assigned_vehicle_plate", e.target.value.toUpperCase())} placeholder="Plate" className="w-20 border border-orange-200 rounded px-1.5 py-1" data-testid={`vehicle-plate-input-${d.id}`}/></td>
                  <td className="p-1 whitespace-nowrap">
                    {d.vehicle_photo_doc_id ? (
                      <button onClick={() => viewPhoto(d)} className="text-emerald-700 underline" data-testid={`view-vehicle-photo-${d.id}`}>✓ view</button>
                    ) : (
                      <label className="text-orange-700 underline cursor-pointer" data-testid={`upload-vehicle-photo-${d.id}`}>
                        {uploadingId === d.id ? "Uploading…" : "snap/upload"}
                        <input type="file" accept="image/*" capture="environment" className="hidden"
                          onChange={e => uploadPhoto(d, e.target.files?.[0])} disabled={uploadingId === d.id} />
                      </label>
                    )}
                  </td>
                  <td className="p-1">
                    <button onClick={() => save(d)} disabled={busyId === d.id || !edits[d.id]} className="text-xs bg-forest text-white px-2.5 py-1 rounded-md disabled:opacity-40" data-testid={`save-vehicle-info-${d.id}`}>
                      {busyId === d.id ? "Saving…" : "Save"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RosterDialog({ tp, drivers, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [docs, setDocs] = useState({});
  const [form, setForm] = useState({
    name: "", phone: "", email: "", vehicle_type: "wheelchair", license_state: "MI",
    // extended credentialing (optional at add-time)
    dob: "", ssn_last4: "", address: "",
    dl_number: "", dl_state: "MI", dl_expiry: "",
    cdl: false, chauffeur_endorsement: false,
    medical_cert_expiry: "", drug_screen_date: "", background_check_date: "",
    mvr_pull_date: "", mvr_consent_date: "",
    emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relationship: "",
    assigned_vehicle_plate: "", vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_vin: "",
  });
  const [showExtended, setShowExtended] = useState(false);
  const [areaOpen, setAreaOpen] = useState({}); // {driver_id: {open: bool, form: {...}}}
  const [selectedIds, setSelectedIds] = useState([]);
  const [qrDriver, setQrDriver] = useState(null); // {name, beacon_url} while QR modal open
  const [bulkBusy, setBulkBusy] = useState(false);
  const tpDrivers = drivers.filter(d => d.tp_id === tp?.tp_id);
  if (!tp) return null;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const selectAllActive = () => {
    const activeIds = tpDrivers.filter((d) => d.status === "active").map((d) => d.id);
    setSelectedIds((prev) => (prev.length === activeIds.length ? [] : activeIds));
  };
  const bulkResend = async () => {
    if (!selectedIds.length) { toast.error("Select at least one driver"); return; }
    if (!window.confirm(`Resend beacon link (SMS + email) to ${selectedIds.length} driver(s)?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await apiClient.post(`${TP}/drivers/bulk-beacon-resend`, { driver_ids: selectedIds });
      const okCount = (data.results || []).filter((r) => r.ok).length;
      toast.success(`Beacon resent to ${okCount}/${data.count} driver(s)`);
      setSelectedIds([]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Bulk resend failed"); }
    finally { setBulkBusy(false); }
  };

  const bulkApproveAll = async () => {
    const pendingDrivers = tpDrivers.filter(d => (d.medexova_credentialing_status || "pending") !== "cleared" && d.status === "active");
    if (!pendingDrivers.length) { toast.info("All active drivers are already credentialed"); return; }
    if (!window.confirm(`Approve all ${pendingDrivers.length} pending/uncleared driver(s) for dispatch?`)) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    try {
      for (const d of pendingDrivers) {
        try {
          await apiClient.patch(`/tpt/drivers/${d.id}/credentialing`, { medexova_credentialing_status: "cleared" });
          ok++;
        } catch { fail++; }
      }
      toast.success(`Credentialed ${ok} driver${ok !== 1 ? "s" : ""}${fail ? ` · ${fail} failed` : ""}`);
      onChanged();
    } finally { setBulkBusy(false); }
  };
  const openQr = async (d) => {
    try {
      const r = await apiClient.post(`${TP}/drivers/${d.id}/beacon-key`);
      setQrDriver({ name: d.name, beacon_url: r.data.beacon_url });
    } catch (e) { toast.error("Could not load beacon QR"); }
  };

  const toggleArea = (d) => {
    setAreaOpen((prev) => {
      const cur = prev[d.id];
      if (cur?.open) return { ...prev, [d.id]: { ...cur, open: false } };
      return {
        ...prev,
        [d.id]: {
          open: true,
          form: {
            service_area_type: d.service_area_type || "radius",
            service_radius_miles: d.service_radius_miles ?? 10,
            service_home_address: d.service_home_address || d.address || "",
            service_zip_codes: Array.isArray(d.service_zip_codes) ? d.service_zip_codes.join(", ") : "",
          },
        },
      };
    });
  };
  const setAreaField = (id, k, v) => {
    setAreaOpen((prev) => ({
      ...prev,
      [id]: { ...prev[id], form: { ...prev[id].form, [k]: v } },
    }));
  };

  const uploadDoc = async (slot, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post(`${TP}/coi-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs(d => ({ ...d, [slot]: { doc_id: data.doc_id, filename: data.filename } }));
      toast.success(`${slot.replace(/_/g, " ")} uploaded`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "upload failed"); }
    finally { setUploading(null); }
  };

  const add = async () => {
    if (!form.name || !form.phone) { toast.error("Name + phone required"); return; }
    if (!isValidE164(form.phone)) { toast.error("Phone must be E.164 (+1XXXXXXXXXX)"); return; }
    if (form.emergency_contact_phone && !isValidE164(form.emergency_contact_phone)) {
      toast.error("Emergency contact phone must be E.164 format"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)),
        photo_doc_id:            docs.photo_doc_id?.doc_id,
        dl_front_doc_id:         docs.dl_front_doc_id?.doc_id,
        dl_back_doc_id:          docs.dl_back_doc_id?.doc_id,
        medical_cert_doc_id:     docs.medical_cert_doc_id?.doc_id,
        drug_screen_doc_id:      docs.drug_screen_doc_id?.doc_id,
        background_check_doc_id: docs.background_check_doc_id?.doc_id,
      };
      Object.keys(payload).forEach(k => payload[k] == null && delete payload[k]);
      await apiClient.post(`${TP}/providers/${tp.tp_id}/drivers`, payload);
      toast.success("Driver added — auto-submitting to MedexOva if complete");
      setForm({
        name: "", phone: "", email: "", vehicle_type: "wheelchair", license_state: "MI",
        dob: "", ssn_last4: "", address: "",
        dl_number: "", dl_state: "MI", dl_expiry: "",
        cdl: false, chauffeur_endorsement: false,
        medical_cert_expiry: "", drug_screen_date: "", background_check_date: "",
        mvr_pull_date: "", mvr_consent_date: "",
        emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relationship: "",
        assigned_vehicle_plate: "", vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_vin: "",
      });
      setDocs({});
      setShowExtended(false);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Add failed"); }
    finally { setBusy(false); }
  };

  const DocSlot = ({ slot, label }) => (
    <div className="border border-dashed border-[#D0CDC0] rounded-md p-2 bg-white">
      <div className="text-[10px] font-semibold text-slate-700 mb-1">{label}</div>
      <input type="file" accept=".pdf,.png,.jpg,.jpeg" disabled={uploading === slot}
        onChange={(e) => uploadDoc(slot, e.target.files?.[0])}
        data-testid={`doc-slot-${slot}`}
        style={{ display: "block", width: "100%", fontSize: 10 }}/>
      {uploading === slot && <div className="text-[10px] text-slate-500 mt-1">Uploading…</div>}
      {docs[slot] && <div className="text-[10px] text-green-700 font-semibold mt-1">✓ {docs[slot].filename.slice(0, 24)}</div>}
    </div>
  );
  const setCred = async (d, status) => {
    try {
      await apiClient.patch(`/tpt/drivers/${d.id}/credentialing`, { medexova_credentialing_status: status });
      toast.success(`${d.name}: ${status}`);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  const beaconUrl = async (d) => {
    try {
      const r = await apiClient.post(`${TP}/drivers/${d.id}/beacon-key`);
      await navigator.clipboard.writeText(r.data.beacon_url);
      toast.success(`Beacon URL copied — send to ${d.name.split(" ")[0]}`);
    } catch (e) { toast.error("Beacon issue failed"); }
  };
  const smsBeacon = async (d) => {
    if (!window.confirm(`Resend the beacon link (SMS + email) to ${d.name}?`)) return;
    try {
      const r = await apiClient.post(`${TP}/drivers/${d.id}/beacon-sms`);
      const via = [r.data.sent?.sms && "SMS", r.data.sent?.email && "email"].filter(Boolean).join(" + ") || "no channel (missing phone/email)";
      toast.success(`Beacon sent to ${d.name.split(" ")[0]} via ${via}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Resend failed"); }
  };
  const credChip = (s) => {
    const map = { cleared: "bg-green-100 text-green-800", pending: "bg-amber-100 text-amber-800", in_review: "bg-blue-100 text-blue-800", rejected: "bg-red-100 text-red-800" };
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${map[s] || "bg-slate-100"}`}>{s || "—"}</span>;
  };
  const saveDriverArea = async (d, patch) => {
    try {
      await apiClient.patch(`/tpt/drivers/${d.id}`, patch);
      toast.success(`Service area saved for ${d.name}`);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
  };
  return (
    <>
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="roster-dialog">
        <div className="text-lg font-bold mb-1">Driver roster · {tp.name}</div>
        <div className="text-xs text-slate-500 mb-4">Add drivers, approve credentialing, issue beacon links.</div>
        {tpDrivers.length > 0 && (
          <div className="flex items-center justify-between gap-2 mb-2 bg-slate-50 border border-[#E9EAE4] rounded-lg px-3 py-2 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" data-testid="select-all-active-drivers"
                checked={selectedIds.length > 0 && selectedIds.length === tpDrivers.filter(d => d.status === "active").length}
                onChange={selectAllActive} />
              Select all active
            </label>
            <div className="flex gap-2">
              {tpDrivers.some(d => (d.medexova_credentialing_status || "pending") !== "cleared" && d.status === "active") && (
                <button onClick={bulkApproveAll} disabled={bulkBusy}
                  className="text-[11px] bg-forest disabled:bg-slate-300 text-white px-3 py-1.5 rounded-md font-medium"
                  data-testid="bulk-approve-all-btn"
                  title="Set all pending/uncleared active drivers to credentialed in one click">
                  {bulkBusy ? "Approving…" : "Approve all pending"}
                </button>
              )}
              <button onClick={bulkResend} disabled={bulkBusy || !selectedIds.length}
                className="text-[11px] bg-blue-600 disabled:bg-slate-300 text-white px-3 py-1.5 rounded-md font-medium"
                data-testid="bulk-resend-beacon-btn">
                {bulkBusy ? "Sending…" : `Resend beacon to ${selectedIds.length || 0} selected`}
              </button>
            </div>
          </div>
        )}
        <div className="border border-[#E9EAE4] rounded-lg mb-4">
          {tpDrivers.length === 0 && <div className="p-3 text-sm text-slate-500">No drivers yet.</div>}
          {tpDrivers.map(d => (
            <div key={d.id} className="p-3 border-b border-[#E9EAE4] last:border-0" data-testid={`driver-row-${d.id}`}>
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={selectedIds.includes(d.id)}
                    onChange={() => toggleSelect(d.id)} data-testid={`select-driver-${d.id}`} />
                  <div>
                    <div className="font-medium text-slate-900">{d.name} {credChip(d.medexova_credentialing_status)}</div>
                    <div className="text-xs text-slate-500">{d.phone} · {d.vehicle_type} · {d.shift_status || "offline"}</div>
                    {d.credentialing_expiring_field && (
                      <div className="mt-1 text-[10px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5 inline-block">
                        ⚠ {d.credentialing_expiring_field} expires in {d.credentialing_expiring_days || "?"} days
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {d.medexova_credentialing_status !== "cleared" && (
                    <button onClick={() => setCred(d, "cleared")} className="text-[11px] bg-forest text-white px-2 py-1 rounded" data-testid={`approve-${d.id}`}>Approve</button>
                  )}
                  {d.medexova_credentialing_status === "cleared" && (
                    <button onClick={() => setCred(d, "rejected")} className="text-[11px] border border-[#E2E0D8] px-2 py-1 rounded">Revoke</button>
                  )}
                  <button onClick={() => toggleArea(d)} className="text-[11px] border border-brandblue/40 text-brandblue px-2 py-1 rounded" data-testid={`driver-area-${d.id}`}>
                    {areaOpen[d.id]?.open ? "Close area" : "Service area"}
                  </button>
                  <button onClick={() => beaconUrl(d)} className="text-[11px] border border-[#E2E0D8] px-2 py-1 rounded" data-testid={`beacon-${d.id}`}>Copy Beacon</button>
                  <button onClick={() => openQr(d)} className="text-[11px] border border-[#E2E0D8] px-2 py-1 rounded flex items-center gap-1" data-testid={`beacon-qr-${d.id}`}>
                    <QrCode size={12} /> QR
                  </button>
                  <button onClick={() => smsBeacon(d)} className="text-[11px] bg-blue-600 text-white px-2 py-1 rounded" data-testid={`beacon-sms-${d.id}`}>Resend Beacon</button>
                  <button
                    onClick={() => saveDriverArea(d, { is_24hr_service: !d.is_24hr_service })}
                    className={`text-[11px] px-2 py-1 rounded ${d.is_24hr_service ? "bg-blue-100 text-blue-800" : "border border-[#E2E0D8]"}`}
                    data-testid={`driver-24hr-toggle-${d.id}`}
                  >
                    {d.is_24hr_service ? "✓ 24hr" : "Mark 24hr"}
                  </button>
                </div>
              </div>
              {areaOpen[d.id]?.open && (
                <div className="mt-2 border-t border-[#EDEBE1] pt-3 space-y-2" data-testid={`driver-area-editor-${d.id}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Area type</div>
                      <select
                        value={areaOpen[d.id].form.service_area_type}
                        onChange={(e) => setAreaField(d.id, "service_area_type", e.target.value)}
                        data-testid={`driver-area-type-${d.id}`}
                        className="w-full border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs"
                      >
                        <option value="radius">Radius from home</option>
                        <option value="zips">ZIP codes only</option>
                        <option value="both">Both</option>
                      </select>
                    </label>
                    {areaOpen[d.id].form.service_area_type !== "zips" && (
                      <label className="block">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Radius (miles)</div>
                        <input
                          type="number"
                          min="1"
                          max="200"
                          value={areaOpen[d.id].form.service_radius_miles ?? ""}
                          onChange={(e) => setAreaField(d.id, "service_radius_miles", e.target.value)}
                          data-testid={`driver-area-radius-${d.id}`}
                          className="w-full border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs"
                        />
                      </label>
                    )}
                  </div>
                  {areaOpen[d.id].form.service_area_type !== "zips" && (
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Radius center address</div>
                      <input
                        type="text"
                        value={areaOpen[d.id].form.service_home_address}
                        onChange={(e) => setAreaField(d.id, "service_home_address", e.target.value)}
                        data-testid={`driver-area-address-${d.id}`}
                        placeholder="123 Main St, City ST 12345"
                        className="w-full border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs"
                      />
                    </label>
                  )}
                  {areaOpen[d.id].form.service_area_type !== "radius" && (
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">ZIPs served (comma-separated)</div>
                      <input
                        type="text"
                        value={areaOpen[d.id].form.service_zip_codes}
                        onChange={(e) => setAreaField(d.id, "service_zip_codes", e.target.value)}
                        data-testid={`driver-area-zips-${d.id}`}
                        placeholder="60620, 60453"
                        className="w-full border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs"
                      />
                    </label>
                  )}
                  {d.service_home_lat != null && d.service_home_lng != null && (
                    <div className="text-[10px] text-slate-500">
                      Currently geocoded: {Number(d.service_home_lat).toFixed(4)}, {Number(d.service_home_lng).toFixed(4)}
                    </div>
                  )}
                  <ZoneOverrideSelect
                    entityType="driver"
                    entityId={d.id}
                    zoneId={d.zone_id}
                    zoneName={d.zone_name}
                    zoneSource={d.zone_source}
                    onSaved={onChanged}
                    testId={`driver-zone-override-${d.id}`}
                  />
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      onClick={() => {
                        const f = areaOpen[d.id].form;
                        saveDriverArea(d, {
                          service_area_type: f.service_area_type,
                          service_radius_miles: f.service_radius_miles === "" ? null : Number(f.service_radius_miles),
                          service_home_address: f.service_home_address || null,
                          service_zip_codes: String(f.service_zip_codes || "")
                            .split(/[,\s]+/)
                            .map((z) => z.trim())
                            .filter((z) => /^\d{5}$/.test(z)),
                        });
                      }}
                      data-testid={`driver-area-save-${d.id}`}
                      className="text-[11px] bg-forest text-white px-3 py-1 rounded font-semibold"
                    >
                      Save area
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Add driver</div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <input value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} placeholder="Full name" className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="add-driver-name"/>
          <PhoneInput value={form.phone} onChange={(v) => setForm({...form, phone:v})} testid="add-driver-phone" required />
          <input value={form.email} onChange={(e) => setForm({...form, email:e.target.value})} placeholder="Email (optional)" className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
          <select value={form.vehicle_type} onChange={(e) => setForm({...form, vehicle_type:e.target.value})} className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm">
            <option value="sedan">Sedan</option><option value="wheelchair">Wheelchair</option>
            <option value="stretcher">Stretcher</option><option value="van">Van</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <input value={form.vehicle_make} onChange={(e) => setForm({...form, vehicle_make:e.target.value})} placeholder="Vehicle make" className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="add-driver-vehicle-make"/>
          <input value={form.vehicle_model} onChange={(e) => setForm({...form, vehicle_model:e.target.value})} placeholder="Vehicle model" className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="add-driver-vehicle-model"/>
          <input value={form.vehicle_year} onChange={(e) => setForm({...form, vehicle_year:e.target.value})} placeholder="Vehicle year" type="number" className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="add-driver-vehicle-year"/>
          <input value={form.vehicle_vin} onChange={(e) => setForm({...form, vehicle_vin:e.target.value.toUpperCase()})} placeholder="VIN" className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="add-driver-vehicle-vin"/>
          <input value={form.assigned_vehicle_plate} onChange={(e) => setForm({...form, assigned_vehicle_plate:e.target.value.toUpperCase()})} placeholder="License plate" className="col-span-2 border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="add-driver-vehicle-plate"/>
        </div>
        <SMSComplianceNotice compact className="mb-3"/>
        <button type="button" onClick={() => setShowExtended(!showExtended)} className="w-full text-left text-xs text-forest font-semibold py-1 mb-2" data-testid="toggle-extended-cred">
          {showExtended ? "▼" : "▶"} Extended credentialing fields (optional at add — required for broker submission)
        </button>
        {showExtended && (
          <div className="mb-3 border border-[#E9EAE4] rounded-md p-3 bg-slate-50 space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Identity</div>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={form.dob} onChange={(e) => setForm({...form, dob:e.target.value})} placeholder="DOB" className="border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs" data-testid="ext-dob"/>
                <input value={form.ssn_last4} onChange={(e) => setForm({...form, ssn_last4:e.target.value.slice(0,4)})} placeholder="SSN last 4" maxLength={4} className="border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs" data-testid="ext-ssn"/>
                <input value={form.address} onChange={(e) => setForm({...form, address:e.target.value})} placeholder="Home address" className="col-span-2 border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs" data-testid="ext-address"/>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Driver's license</div>
              <div className="grid grid-cols-3 gap-2">
                <input value={form.dl_number} onChange={(e) => setForm({...form, dl_number:e.target.value})} placeholder="DL #" className="border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs" data-testid="ext-dl-number"/>
                <input value={form.dl_state} onChange={(e) => setForm({...form, dl_state:e.target.value.toUpperCase().slice(0,2)})} placeholder="State" maxLength={2} className="border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs" data-testid="ext-dl-state"/>
                <input type="date" value={form.dl_expiry} onChange={(e) => setForm({...form, dl_expiry:e.target.value})} className="border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs" data-testid="ext-dl-expiry"/>
              </div>
              <div className="flex gap-4 mt-2 text-xs">
                <label className="flex items-center gap-1"><input type="checkbox" checked={form.cdl} onChange={(e) => setForm({...form, cdl:e.target.checked})} data-testid="ext-cdl"/> CDL</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={form.chauffeur_endorsement} onChange={(e) => setForm({...form, chauffeur_endorsement:e.target.checked})} data-testid="ext-chauffeur"/> Chauffeur endorsement</label>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Medical & background</div>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] text-slate-500">Medical cert expiry<input type="date" value={form.medical_cert_expiry} onChange={(e) => setForm({...form, medical_cert_expiry:e.target.value})} className="w-full border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs mt-0.5"/></label>
                <label className="text-[10px] text-slate-500">Drug screen<input type="date" value={form.drug_screen_date} onChange={(e) => setForm({...form, drug_screen_date:e.target.value})} className="w-full border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs mt-0.5"/></label>
                <label className="text-[10px] text-slate-500">Background check<input type="date" value={form.background_check_date} onChange={(e) => setForm({...form, background_check_date:e.target.value})} className="w-full border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs mt-0.5"/></label>
                <label className="text-[10px] text-slate-500">MVR pull<input type="date" value={form.mvr_pull_date} onChange={(e) => setForm({...form, mvr_pull_date:e.target.value})} className="w-full border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs mt-0.5"/></label>
                <label className="text-[10px] text-slate-500 col-span-2">MVR consent signed on<input type="date" value={form.mvr_consent_date} onChange={(e) => setForm({...form, mvr_consent_date:e.target.value})} className="w-full border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs mt-0.5"/></label>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Emergency contact</div>
              <div className="grid grid-cols-3 gap-2">
                <input value={form.emergency_contact_name} onChange={(e) => setForm({...form, emergency_contact_name:e.target.value})} placeholder="Name" className="border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs" data-testid="ext-ec-name"/>
                <input value={form.emergency_contact_phone} onChange={(e) => setForm({...form, emergency_contact_phone:e.target.value})} placeholder="Phone (+1…)" className="border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs" data-testid="ext-ec-phone"/>
                <input value={form.emergency_contact_relationship} onChange={(e) => setForm({...form, emergency_contact_relationship:e.target.value})} placeholder="Relationship" className="border border-[#E2E0D8] rounded-md px-2 py-1.5 text-xs" data-testid="ext-ec-rel"/>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Credentialing documents (PDF/JPG/PNG, max 15 MB each)</div>
              <div className="grid grid-cols-2 gap-2">
                <DocSlot slot="photo_doc_id" label="Driver photo (headshot)"/>
                <DocSlot slot="dl_front_doc_id" label="DL — front scan"/>
                <DocSlot slot="dl_back_doc_id" label="DL — back scan"/>
                <DocSlot slot="medical_cert_doc_id" label="Medical examiner's cert"/>
                <DocSlot slot="drug_screen_doc_id" label="Drug screen result"/>
                <DocSlot slot="background_check_doc_id" label="Background check report"/>
              </div>
              <div className="text-[10px] text-slate-500 mt-2">
                Uploaded docs are pushed to MedexOva as part of the credentialing dossier. MedexOva tracks each expiry and sends webhook alerts 30/60/90 days out — you don&apos;t manage that here.
              </div>
            </div>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Close</button>
          <button onClick={add} disabled={busy} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold" data-testid="add-driver-submit">Add driver</button>
        </div>
      </div>
    </div>
    {qrDriver && (
      <div className="fixed inset-0 bg-black/60 z-[10000] grid place-items-center p-4" onClick={() => setQrDriver(null)} data-testid="beacon-qr-modal">
        <div className="bg-white rounded-2xl p-6 max-w-xs w-full text-center relative" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setQrDriver(null)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700" data-testid="beacon-qr-close">
            <X size={18} />
          </button>
          <div className="text-sm font-bold text-slate-900 mb-1">{qrDriver.name}'s beacon</div>
          <div className="text-[11px] text-slate-500 mb-4">Scan with the driver's phone camera to open their beacon instantly.</div>
          <div className="flex justify-center mb-4 p-3 bg-white border border-[#E9EAE4] rounded-lg inline-block">
            <QRCodeSVG value={qrDriver.beacon_url} size={180} />
          </div>
          <div className="text-[11px] font-mono break-all text-slate-600 bg-slate-50 rounded-md p-2">{qrDriver.beacon_url}</div>
        </div>
      </div>
    )}
    </>
  );
}

function KaizenSyncButton({ onSynced }) {
  const [busy, setBusy] = useState(false);
  const [showSnaps, setShowSnaps] = useState(false);
  const [snapshots, setSnapshots] = useState({ providers: [], drivers: [] });
  const [snapKind, setSnapKind] = useState("providers");

  const capture = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/tpt/kaizen-credentials/capture");
      if (data.ok) {
        toast.success(`Captured ${data.providers_captured} providers · ${data.drivers_captured} drivers`);
        openSnapshots();
        onSynced?.();
      } else {
        toast.error(data.error || "Kaizen sync failed");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Kaizen sync failed");
    } finally { setBusy(false); }
  };

  const openSnapshots = async () => {
    setShowSnaps(true);
    try {
      const [p, d] = await Promise.all([
        apiClient.get("/tpt/kaizen-credentials/snapshots", { params: { kind: "providers" } }),
        apiClient.get("/tpt/kaizen-credentials/snapshots", { params: { kind: "drivers" } }),
      ]);
      setSnapshots({ providers: p.data.snapshots || [], drivers: d.data.snapshots || [] });
    } catch { /* silent */ }
  };

  const listed = snapKind === "providers" ? snapshots.providers : snapshots.drivers;

  return (
    <>
      <button
        onClick={capture}
        disabled={busy}
        className="border border-[#E2E0D8] text-forest px-3 py-2 rounded-md text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
        data-testid="kaizen-sync-btn"
        title="Scrape provider + driver credentials from Kaizen portal"
      >
        {busy ? "Syncing…" : "Sync from Kaizen"}
      </button>
      <button
        onClick={openSnapshots}
        className="border border-[#E2E0D8] text-slate-600 px-3 py-2 rounded-md text-sm hover:bg-slate-50"
        data-testid="kaizen-snapshots-btn"
        title="View last Kaizen snapshot"
      >
        Snapshots
      </button>
      {showSnaps && (
        <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={() => setShowSnaps(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="kaizen-snapshots-dialog">
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-bold text-slate_navy">Kaizen credential snapshots</div>
              <button onClick={() => setShowSnaps(false)} className="text-slate-500">✕</button>
            </div>
            <div className="text-xs text-slate-500 mb-3">
              Read-only view of what the Kaizen portal exposes. Use these to reconcile local TP + driver records against Kaizen&apos;s approved list.
            </div>
            <div className="flex gap-2 mb-3">
              {["providers", "drivers"].map(k => (
                <button key={k} onClick={() => setSnapKind(k)} className={`text-xs px-3 py-1.5 rounded-md ${snapKind === k ? "bg-forest text-white" : "border border-[#E2E0D8]"}`} data-testid={`snap-kind-${k}`}>
                  {k.charAt(0).toUpperCase() + k.slice(1)} ({snapshots[k]?.length || 0})
                </button>
              ))}
            </div>
            {listed.length === 0 ? (
              <div className="text-xs text-slate-500 border border-dashed border-[#E2E0D8] p-6 rounded-md text-center">
                No snapshots yet — click &ldquo;Sync from Kaizen&rdquo; to capture. First run will need selector tuning if your Kaizen DOM differs.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="text-left px-2 py-1">Name</th>
                    <th className="text-left px-2 py-1">Status</th>
                    {snapKind === "drivers" && <th className="text-left px-2 py-1">License</th>}
                    <th className="text-left px-2 py-1">Expiry</th>
                    <th className="text-left px-2 py-1">Contact</th>
                    <th className="text-left px-2 py-1">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1EFE8]">
                  {listed.map((s, i) => (
                    <tr key={s.key + i} className="hover:bg-slate-50" data-testid={`kaizen-snap-row-${i}`}>
                      <td className="px-2 py-1 font-medium">{s.name || "—"}</td>
                      <td className="px-2 py-1"><span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100">{s.status || "—"}</span></td>
                      {snapKind === "drivers" && <td className="px-2 py-1 font-mono text-slate-500">{s.license_number || "—"}</td>}
                      <td className="px-2 py-1 text-slate-500">{s.expiry || s.license_expiry || "—"}</td>
                      <td className="px-2 py-1 text-slate-500">{s.email || s.phone || "—"}</td>
                      <td className="px-2 py-1 text-slate-500">{s.updated_at ? new Date(s.updated_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}


export default function ProvidersPanel() {
  const [providers, setProviders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [editing, setEditing] = useState(null);   // null | object | "new"
  const [managingUsers, setManagingUsers] = useState(null);
  const [rosterFor, setRosterFor] = useState(null);
  const [onboardingState, setOnboardingState] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [managingTier, setManagingTier] = useState(null);

  const load = useCallback(async () => {
    // Use allSettled so a slow /applications call doesn't blow up the whole panel
    const [pR, dR, aR] = await Promise.allSettled([
      apiClient.get(`${TP}/providers`),
      apiClient.get(`/drivers`),
      apiClient.get(`${TP}/applications`),
    ]);
    if (pR.status === "fulfilled") setProviders(pR.value.data || []); else toast.error("Providers load failed");
    if (dR.status === "fulfilled") setDrivers(dR.value.data || []); else toast.warning("Driver roster load failed");
    if (aR.status === "fulfilled") setApplications(aR.value.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const roster = useMemo(() => {
    const map = {};
    for (const d of drivers) {
      if (d.tp_id) (map[d.tp_id] = map[d.tp_id] || []).push(d);
    }
    return map;
  }, [drivers]);

  const archivedCount = useMemo(() => providers.filter((p) => p.status === "archived").length, [providers]);
  const visibleProviders = useMemo(
    () => providers.filter((p) => showArchived || p.status !== "archived"),
    [providers, showArchived]
  );

  const archiveProvider = async (tp) => {
    const ok = await confirm({
      title: `Archive ${tp.name}?`,
      body: "Hides it from the active list without deleting its history. You can unarchive it anytime.",
      confirmLabel: "Archive",
      tone: "primary",
    });
    if (!ok) return;
    try {
      await apiClient.patch(`${TP}/providers/${tp.tp_id}`, { status: "archived" });
      toast.success(`${tp.name} archived`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Archive failed");
    }
  };

  const unarchiveProvider = async (tp) => {
    try {
      await apiClient.patch(`${TP}/providers/${tp.tp_id}`, { status: "active" });
      toast.success(`${tp.name} restored`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Restore failed");
    }
  };

  const deleteProvider = async (tp) => {
    const ok = await confirm({
      title: `Delete ${tp.name}?`,
      body: "Permanent — only allowed if it has no drivers, dispatchers, trips, or invoices. Use Archive instead if you just want it out of the active list.",
      confirmLabel: "Delete permanently",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await apiClient.delete(`${TP}/providers/${tp.tp_id}`);
      toast.success(`${tp.name} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate_navy">Transportation Providers</h2>
          <p className="text-sm text-slate-500">Onboard new providers, edit contract rules, review applications, and manage their dispatcher accounts.</p>
        </div>
        <div className="flex gap-2 items-center">
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived((s) => !s)}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-2"
              data-testid="toggle-show-archived-tp"
            >
              {showArchived ? "Hide" : "Show"} archived ({archivedCount})
            </button>
          )}
          <KaizenSyncButton onSynced={load}/>
          <button onClick={() => setEditing("new")} className="bg-forest text-white px-4 py-2 rounded-md text-sm font-semibold" data-testid="new-tp-btn">+ New provider</button>
        </div>
      </div>

      <ApplicationsPanel applications={applications} onChange={load}/>
      <MissingVehicleInfoPanel drivers={drivers} onChanged={load}/>

      <div className="grid md:grid-cols-2 gap-4">
        {visibleProviders.map(tp => (
          <div key={tp.tp_id} className={`bg-white border border-[#E9EAE4] rounded-xl p-4 ${tp.status === "archived" ? "opacity-60" : ""}`} data-testid={`tp-card-${tp.tp_id}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-semibold text-slate_navy">{tp.name}</div>
                <div className="text-xs text-slate-500">{tp.contact_phone || "no phone"} · {tp.contact_email || "no email"}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                tp.status === "active" ? "bg-green-100 text-green-800"
                : tp.status === "archived" ? "bg-slate-100 text-slate-500 border border-dashed border-slate-300"
                : "bg-slate-200 text-slate-700"
              }`}>
                {tp.status}
              </span>
            </div>
            <button
              onClick={async () => { await apiClient.patch(`${TP}/providers/${tp.tp_id}`, { is_24hr_service: !tp.is_24hr_service }); toast.success(`${tp.name}: 24hr service ${!tp.is_24hr_service ? "on" : "off"}`); load(); }}
              className={`mt-2 text-[10px] px-2 py-0.5 rounded-full font-semibold ${tp.is_24hr_service ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-500"}`}
              data-testid={`tp-24hr-toggle-${tp.tp_id}`}
            >
              {tp.is_24hr_service ? "✓ 24hr / CPD after-hours" : "Mark 24hr / CPD after-hours"}
            </button>
            <div className="mt-2" data-testid={`tp-portal-status-${tp.tp_id}`}>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${tp.portal_activated ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>
                Portal: {tp.portal_activated ? "Activated" : "Not activated"}
                {tp.portal_dispatcher_count > 0 ? ` · ${tp.portal_dispatcher_count} dispatcher${tp.portal_dispatcher_count === 1 ? "" : "s"}` : ""}
              </span>
              {tp.portal_last_login_at && (
                <span className="ml-2 text-[10px] text-slate-400">last login {new Date(tp.portal_last_login_at).toLocaleDateString()}</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mt-3">
              <div>Wait: <b>{tp.wait_time_minutes}min</b></div>
              <div>No-show: <b>{tp.no_show_wait_minutes}min</b></div>
              <div>Offer TTL: <b>{tp.offer_timeout_seconds}s</b></div>
              <div>Lead: <b>{tp.dispatch_lead_time_minutes || 60}min</b></div>
              <div>Dead miles: <b>{tp.pays_dead_miles ? `$${(tp.dead_mile_rate||0).toFixed(2)}/mi` : "off"}</b></div>
              <div>Auto-assign: <b className={tp.auto_assign_enabled ? "text-forest" : "text-slate-500"}>{tp.auto_assign_enabled ? tp.auto_assign_strategy : "off"}</b></div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              <b>{(roster[tp.tp_id] || []).length}</b> driver(s)
              {(roster[tp.tp_id] || []).slice(0, 3).map(d => <span key={d.id} className="ml-2">· {d.name}</span>)}
            </div>
            <OnboardingChip tpId={tp.tp_id} onOpen={setOnboardingState}/>
            <div className="mt-4 flex gap-2 text-xs flex-wrap">
              <button onClick={() => setEditing(tp)} className="border border-[#E2E0D8] px-3 py-1 rounded-md" data-testid={`edit-tp-${tp.tp_id}`}>Edit</button>
              <button onClick={() => setRosterFor(tp)} className="border border-[#E2E0D8] px-3 py-1 rounded-md" data-testid={`roster-tp-${tp.tp_id}`}>Roster</button>
              <button onClick={() => setManagingUsers(tp)} className="border border-[#E2E0D8] px-3 py-1 rounded-md" data-testid={`users-tp-${tp.tp_id}`}>Dispatchers</button>
              <JumpInButton tp={tp}/>
              <button onClick={() => setManagingTier(tp)} className="border border-amber-300 text-amber-700 px-3 py-1 rounded-md" data-testid={`portal-tier-tp-${tp.tp_id}`}>Portal tier</button>
              <SendContractButton tp={tp}/>
              <SubmitTPButton tp={tp}/>
              <TempApproveButton tp={tp} onRefresh={load}/>
              {tp.status === "archived" ? (
                <button onClick={() => unarchiveProvider(tp)} className="border border-forest text-forest px-3 py-1 rounded-md" data-testid={`unarchive-tp-${tp.tp_id}`}>Unarchive</button>
              ) : (
                <button onClick={() => archiveProvider(tp)} className="border border-[#E2E0D8] px-3 py-1 rounded-md" data-testid={`archive-tp-${tp.tp_id}`}>Archive</button>
              )}
              <button onClick={() => deleteProvider(tp)} className="border border-red-200 text-red-600 px-3 py-1 rounded-md hover:bg-red-50" data-testid={`delete-tp-${tp.tp_id}`}>Delete</button>
            </div>
            {tp.tp_credentialing_expiring_field && (
              <div className="mt-2 text-[10px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1" data-testid={`tp-expiry-warn-${tp.tp_id}`}>
                <b>Expiring:</b> {tp.tp_credentialing_expiring_field} in {tp.tp_credentialing_expiring_days || "?"} days
              </div>
            )}
            {(tp.medexova_tp_rejection_reasons || []).length > 0 && (
              <div className="mt-2 text-[10px] text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1">
                <b>Rejected:</b> {tp.medexova_tp_rejection_reasons.join(" · ")}
              </div>
            )}
            {/* Driver compliance gap — shown only when TP is temp-approved so VA knows
                which individual drivers still need credentialing to appear on the map */}
            {(() => {
              const now = new Date();
              const until = tp.temp_cleared_until ? new Date(tp.temp_cleared_until) : null;
              if (!until || until <= now) return null;
              const tpDrivers = roster[tp.tp_id] || [];
              if (tpDrivers.length === 0) return (
                <div className="mt-2 text-[10px] text-amber-900 bg-amber-50 border border-amber-300 rounded px-2 py-1.5" data-testid={`tp-driver-cred-warn-${tp.tp_id}`}>
                  <b>No drivers on roster</b> — add and credential drivers before dispatching.
                </div>
              );
              const uncleared = tpDrivers.filter(d => (d.medexova_credentialing_status || "pending") !== "cleared");
              if (uncleared.length === 0) return (
                <div className="mt-2 text-[10px] text-green-800 bg-green-50 border border-green-200 rounded px-2 py-1" data-testid={`tp-driver-cred-ok-${tp.tp_id}`}>
                  All {tpDrivers.length} driver{tpDrivers.length !== 1 ? "s" : ""} individually credentialed — fully dispatchable under temp approval.
                </div>
              );
              const names = uncleared.slice(0, 2).map(d => d.name).join(", ");
              const extra = uncleared.length > 2 ? ` +${uncleared.length - 2} more` : "";
              return (
                <div className="mt-2 text-[10px] text-amber-900 bg-amber-50 border border-amber-300 rounded px-2 py-1.5" data-testid={`tp-driver-cred-warn-${tp.tp_id}`}>
                  <b>Driver compliance gap:</b> {uncleared.length}/{tpDrivers.length} driver{uncleared.length !== 1 ? "s" : ""} not individually credentialed ({names}{extra}) — they won't receive broadcasts until cleared individually.
                </div>
              );
            })()}
          </div>
        ))}
        {visibleProviders.length === 0 && <div className="text-sm text-slate-500 col-span-full">No providers yet.</div>}
      </div>

      {editing && <TPEditor tp={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={load}/>}
      {managingUsers && <TPUserDialog tp={managingUsers} onClose={() => setManagingUsers(null)}/>}
      {rosterFor && <RosterDialog tp={rosterFor} drivers={drivers} onClose={() => setRosterFor(null)} onChanged={load}/>}
      {onboardingState && <OnboardingDialog state={onboardingState} onClose={() => setOnboardingState(null)}/>}
      {managingTier && <PortalTierDialog tp={managingTier} onClose={() => setManagingTier(null)}/>}
    </div>
  );
}

function CredentialsModal({ creds, onClose, onCopy }) {
  if (!creds) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[10000] grid place-items-center p-4" onClick={onClose} data-testid="tp-credentials-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
          <div>
            <div className="font-bold text-slate_navy">TP approved</div>
            <div className="text-xs text-slate-500">{creds.tp_name}</div>
          </div>
        </div>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-900 mb-4">
          <b>Show this ONCE.</b> The temp password will not be shown again. Copy it now and send to the applicant.
        </div>
        <div className="space-y-3 mb-4">
          <div>
            <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Portal URL</div>
            <div className="font-mono text-sm bg-slate-50 border border-[#E2E0D8] rounded-md px-3 py-2" data-testid="cred-portal-url">{creds.portal_url}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Dispatcher email</div>
            <div className="font-mono text-sm bg-slate-50 border border-[#E2E0D8] rounded-md px-3 py-2" data-testid="cred-email">{creds.email}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Temporary password</div>
            <div className="font-mono text-sm bg-slate-50 border border-[#E2E0D8] rounded-md px-3 py-2 text-forest font-bold" data-testid="cred-password">{creds.password}</div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Close</button>
          <button onClick={onCopy} className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold" data-testid="cred-copy-btn">
            <Copy size={14} /> Copy full welcome message
          </button>
        </div>
      </div>
    </div>
  );
}

